import { Users, Heart, ShieldCheck, Globe, Lock, CheckCircle2, XCircle, AlertTriangle, UserX, Image, Hand, AlertOctagon, Skull, Flag } from 'lucide-react';
import LegalLayout from '../../components/LegalLayout';

function GuidelineCard({ title, items, positive }) {
  const Icon = positive ? CheckCircle2 : XCircle;
  return (
    <section
      className={`rounded-2xl p-5 sm:p-6 border ${
        positive ? 'bg-[#F1F8E9] border-[#D8E8C2]' : 'bg-[#FFEBEE] border-[#FFD1D5]'
      }`}
      data-testid={`guideline-${positive ? 'do' : 'dont'}-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 30)}`}
    >
      <div className="flex items-center gap-3 mb-3">
        <Icon className={`w-6 h-6 ${positive ? 'text-[#2A9D8F]' : 'text-[#E76F51]'}`} />
        <h2 className="text-lg font-bold text-[#2B2D42]">{title}</h2>
      </div>
      <ul className="space-y-2">
        {items.map((item, i) => (
          <li key={i} className="flex items-start gap-2 text-sm text-[#3A3D52] leading-relaxed">
            <span className={`mt-0.5 flex-shrink-0 ${positive ? 'text-[#2A9D8F]' : 'text-[#E76F51]'}`}>
              {positive ? '✓' : '✕'}
            </span>
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function ReportTag({ icon: Icon, label }) {
  return (
    <div className="inline-flex items-center gap-1.5 bg-[#F4F1DE] px-3 py-1.5 rounded-lg text-xs font-semibold text-[#3A3D52]">
      <Icon className="w-3.5 h-3.5 text-[#6D6875]" />
      {label}
    </div>
  );
}

export default function CommunityGuidelines() {
  return (
    <LegalLayout
      title="Community Guidelines"
      badge="Community Standards"
      badgeIcon={Users}
      badgeColor="#5A8DBF"
      lastUpdated="April 5, 2026"
      version="1.0"
      introBorderColor="#5A8DBF"
      intro="AfroConnect is built on respect, authenticity, and celebrating African heritage. These guidelines help create a safe, welcoming community for everyone."
    >
      {/* Core values */}
      <section className="bg-white border border-[#F4F1DE] rounded-2xl p-5 sm:p-6">
        <h2 className="text-lg font-bold text-[#2B2D42] mb-4">Our Core Values</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="flex items-center gap-3">
            <Heart className="w-5 h-5 text-[#C8553D]" />
            <span className="text-sm font-semibold text-[#3A3D52]">Respect & Kindness</span>
          </div>
          <div className="flex items-center gap-3">
            <ShieldCheck className="w-5 h-5 text-[#2A9D8F]" />
            <span className="text-sm font-semibold text-[#3A3D52]">Authenticity & Honesty</span>
          </div>
          <div className="flex items-center gap-3">
            <Globe className="w-5 h-5 text-[#5A8DBF]" />
            <span className="text-sm font-semibold text-[#3A3D52]">Cultural Pride & Inclusion</span>
          </div>
          <div className="flex items-center gap-3">
            <Lock className="w-5 h-5 text-[#C8553D]" />
            <span className="text-sm font-semibold text-[#3A3D52]">Safety & Privacy</span>
          </div>
        </div>
      </section>

      <GuidelineCard
        positive
        title="DO: Be Your Authentic Self"
        items={[
          'Use recent photos that clearly show your face',
          'Write an honest bio that reflects who you are',
          'Be truthful about your intentions',
          'Respect cultural differences and backgrounds',
          'Celebrate our shared African heritage',
        ]}
      />

      <GuidelineCard
        title="DON'T: Misrepresent Yourself"
        items={[
          'Use fake or stolen photos',
          'Lie about your age, location, or identity',
          'Impersonate another person',
          'Create multiple accounts',
          "Use someone else's photos without permission",
        ]}
      />

      <GuidelineCard
        positive
        title="DO: Communicate Respectfully"
        items={[
          'Treat everyone with kindness and respect',
          'Accept rejection gracefully',
          'Ask before moving conversations off-app',
          'Report suspicious behavior to keep the community safe',
          'Give people a chance to respond — be patient',
        ]}
      />

      <GuidelineCard
        title="DON'T: Engage in Harmful Behavior"
        items={[
          'Send unwanted explicit messages or photos',
          'Harass, stalk, or bully other users',
          'Use hate speech, slurs, or discriminatory language',
          'Threaten or intimidate anyone',
          'Share private conversations without consent',
        ]}
      />

      <GuidelineCard
        positive
        title="DO: Keep the Community Safe"
        items={[
          'Report fake profiles and scammers',
          'Block users who make you uncomfortable',
          'Verify your profile for added trust',
          'Meet in public places for first dates',
          'Tell a friend about your plans when meeting someone',
        ]}
      />

      <GuidelineCard
        title="DON'T: Spam or Scam"
        items={[
          'Solicit money or financial information',
          'Promote products, services, or businesses in chats',
          'Share external links for commercial purposes',
          'Ask for gift cards, wire transfers, or crypto',
          'Claim false emergencies to get money',
        ]}
      />

      {/* Consequences */}
      <section className="bg-[#FFF3E0] border border-[#FFE1B8] rounded-2xl p-5 sm:p-6 text-center">
        <AlertTriangle className="w-8 h-8 text-[#E76F51] mx-auto mb-3" />
        <h2 className="text-lg font-bold text-[#2B2D42] mb-3">Violations Have Consequences</h2>
        <p className="text-sm text-[#3A3D52] leading-relaxed mb-3">Breaking these guidelines may result in:</p>
        <ul className="text-sm text-[#3A3D52] space-y-1 inline-block text-left">
          <li>• Warning on your account</li>
          <li>• Temporary suspension</li>
          <li>• Permanent ban</li>
          <li>• Removal of content</li>
          <li>• Report to law enforcement (for illegal activity)</li>
        </ul>
      </section>

      {/* Report */}
      <section className="bg-white border border-[#F4F1DE] rounded-2xl p-5 sm:p-6">
        <h2 className="text-lg font-bold text-[#2B2D42] mb-2">See Something? Report It.</h2>
        <p className="text-sm text-[#6D6875] mb-4 leading-relaxed">
          Help keep AfroConnect safe. If you encounter behavior that violates these guidelines, report it immediately. Your reports are confidential.
        </p>
        <div className="flex flex-wrap gap-2">
          <ReportTag icon={UserX} label="Fake Profile" />
          <ReportTag icon={Image} label="Inappropriate Photos" />
          <ReportTag icon={Hand} label="Harassment" />
          <ReportTag icon={AlertOctagon} label="Spam/Scam" />
          <ReportTag icon={AlertTriangle} label="Underage User" />
          <ReportTag icon={Skull} label="Offensive Content" />
          <ReportTag icon={Flag} label="Impersonation" />
        </div>
      </section>

      <div className="text-center pt-4 flex items-center justify-center gap-2 text-[#6D6875] text-sm">
        <Users className="w-4 h-4 text-[#5A8DBF]" />
        <span>Together, we build a safer community</span>
      </div>
    </LegalLayout>
  );
}
